import type { BuyerSubmissionStatus } from "@/api/buyers";
import { buyerLabel } from "./buyerFilters";
import { submissionStatusTone } from "./buyerListBits";
import { definePhaseLadder, type PhaseDef } from "./phaseLadder";

/**
 * The submission lifecycle's SEVENTEEN statuses (L17), condensed to six chips.
 *
 * <p>The FE twin of <c>BuyerSubmissionPhases</c> (Core/Common) — keep the keys,
 * the labels and the membership IDENTICAL, and the shared fixture
 * <c>tests/fixtures/buyer-status-vocabulary.json</c> is what enforces it.</p>
 *
 * <p>🔴 <b>The groupings are the RANK PAIRS from
 * <c>BuyerSubmissionProgress</c>, not a fresh opinion.</b> Its twelve
 * non-terminal ranks fall into six consecutive pairs, and every boundary between
 * them is a genuine hand-off — the buyer decides, then the broker is approached,
 * then the package moves, then documents arrive, then an offer is made, then it
 * ends. Drawing the chips anywhere else would give the module two disagreeing
 * accounts of the same funnel, one of which decides the derived buyer stage.</p>
 *
 * <p>⚠️ The rank table is the ordering authority; this file only groups it.
 * <b>Neither belongs in an EF expression tree</b> — both are dictionary lookups
 * in C# and neither can be translated to SQL.</p>
 */
const PHASES: readonly PhaseDef<BuyerSubmissionStatus>[] = [
  {
    key: "selection",
    label: "Selection",
    // Ranks 1–2. Phoenix and the buyer deciding this listing is worth pursuing;
    // nobody outside the firm has been contacted yet.
    statuses: ["Shortlisted", "BuyerApproved"],
  },
  {
    key: "outreach",
    label: "Outreach",
    // Ranks 3–4. Reaching the LISTING BROKER and hearing back.
    statuses: ["OutreachSent", "BrokerResponded"],
  },
  {
    key: "package",
    label: "Package",
    // Ranks 5–6. Drafted, then out. Generation is deliberately off the send path,
    // so "drafted, being reviewed" is a state a specialist sits in for days — one
    // chip, two badges, which is exactly the split this ladder is built to keep.
    statuses: ["PackageGenerated", "PackageSent"],
  },
  {
    key: "diligence",
    label: "Diligence",
    // Ranks 7–8. The CIM arrives, then the conversation happens.
    statuses: ["InfoReceived", "CallMeeting"],
  },
  {
    key: "offer",
    label: "Offer",
    // Ranks 9–10.
    statuses: ["OfferLoi", "UnderContract"],
  },
  {
    key: "won",
    label: "Won",
    // Ranks 11–12 — the two best ends a submission has.
    //
    // 🔴 EscalatedToDeal belongs HERE and not with the off-ladder five. It is
    // terminal in the sense that nothing in the buyer module advances past it,
    // but it ranks at the TOP of the funnel: a buyer holding one has a live
    // Phoenix deal, and the deal's own progress is read through the pointer.
    // Putting it off the ladder would paint the best outcome like a dead one —
    // which is the exact confusion its enum documentation warns about.
    statuses: ["ClosedPurchased", "EscalatedToDeal"],
  },
];

/**
 * The five terminal statuses — <c>BuyerSubmissionProgress.TerminalRank</c>'s set.
 *
 * <p>🔴 Held as an explicit list rather than inferred from the badge tone: a
 * display colour is not a lifecycle fact, and keying anything off one breaks
 * silently the first time a tone is restyled. The server requires a reason for
 * every one of them.</p>
 */
const OFF_LADDER: readonly BuyerSubmissionStatus[] = [
  "DeclinedByBuyer",
  "NoResponse",
  "RejectedByBroker",
  "Passed",
  "Dead",
];

/**
 * Which statuses close a submission out, and so require a reason on the status
 * change. Identical to the off-ladder set by construction — one definition, so
 * "ended" and "not on the ladder" can never come to disagree.
 */
export const SUBMISSION_TERMINAL_STATUSES: readonly BuyerSubmissionStatus[] = OFF_LADDER;

/**
 * Which milestone a status change stamps, server-side.
 *
 * <p>🔴 The stamp is a SIDE EFFECT of the status move with no control of its own,
 * so it is shown rather than hidden. Stamping is forward-only (<c>??=</c>), so
 * revisiting a status never overwrites the earlier, truer date — and counting
 * always reads the STAMP, never the current status, which is why a submission
 * that later dies keeps the credit it earned.</p>
 *
 * <p>Statuses absent from this map stamp nothing: the terminal five did not reach
 * a milestone by dying, and the package pair are stamped by the package actions.
 * The keys are pinned against the server's <c>StampMilestoneFor</c> itself, so a
 * note here that promises a stamp the server does not write fails the build.</p>
 */
export const SUBMISSION_MILESTONE_FOR: Partial<Record<BuyerSubmissionStatus, string>> = {
  BuyerApproved: "Buyer approved",
  BrokerResponded: "Broker responded",
  // 🔴 CIM ONLY. "Info received" covers the CIM and/or the financials, and the
  // server deliberately stamps just the CIM — claiming both would record
  // documents nobody has seen.
  InfoReceived: "CIM received",
  CallMeeting: "Call / meeting",
  OfferLoi: "Offer / LOI",
  UnderContract: "Under contract",
  ClosedPurchased: "Closed",
};

function noteFor(status: BuyerSubmissionStatus): string {
  switch (status) {
    case "RejectedByBroker":
      return "The listing broker closed it out. The reason is on the status change.";
    case "NoResponse":
      return "It went quiet rather than being declined. The reason is on the status change.";
    default:
      return "Closed out before an offer. The reason is on the status change.";
  }
}

export const submissionPhaseLadder = definePhaseLadder<BuyerSubmissionStatus>({
  phases: PHASES,
  offLadder: OFF_LADDER,
  labelOf: buyerLabel,
  toneOf: submissionStatusTone,
  offLadderNote: noteFor,
});

/** The chip a submission's status rolls up to; `null` when it is off the ladder. */
export const submissionPhaseFor = submissionPhaseLadder.phaseFor;

/** Everything a render needs — chips, the current rung, the raw badge. */
export const describeSubmissionPhase = submissionPhaseLadder.describe;
